import React from 'react';
import { Paper, Typography, Box, Grid, Avatar, Stack, Chip } from '@mui/material';
import { FavoriteBorder, Inventory2 } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import useCurrency from '../../../hooks/common/useCurrency';
import { useGetCustomerWishlist } from '../../../hooks/customers/useGetCustomerWishlist';

const ProductCard = ({ item, isArabic, lng, formatPrice }) => {
    const product = item.product || item;
    const productName =
        product?.name?.[lng] || product?.name?.en || product?.name?.ar || product?.name || '-';
    const image = product?.image?.thumb || product?.image_url || product?.cover_url;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
            }}
            component={product?.id ? RouterLink : 'div'}
            to={product?.id ? `/products/${product.id}/edit` : undefined}
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
        >
            <Avatar
                src={image || undefined}
                variant="rounded"
                sx={{
                    width: '100%',
                    height: 140,
                    bgcolor: 'action.hover',
                    mb: 1.5,
                }}
            >
                <Inventory2 sx={{ fontSize: 36 }} />
            </Avatar>

            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                {productName}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                {product?.price != null && (
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatPrice(product.price)}
                    </Typography>
                )}
                {product?.sku && (
                    <Chip
                        size="small"
                        label={product.sku}
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20 }}
                    />
                )}
            </Stack>
        </Paper>
    );
};

const CustomerWishlistTab = ({ customerId }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng.startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const { items, isLoading } = useGetCustomerWishlist(customerId);

    if (isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                </Typography>
            </Box>
        );
    }

    if (items.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    border: (t) => `1px dashed ${t.palette.divider}`,
                    textAlign: 'center',
                }}
            >
                <FavoriteBorder sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? 'قائمة الرغبات فارغة' : 'Wishlist is empty'}
                </Typography>
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            {items.map((item) => (
                <Grid key={item.id || item.product?.id} item xs={6} sm={4} md={3}>
                    <ProductCard item={item} isArabic={isArabic} lng={lng} formatPrice={formatPrice} />
                </Grid>
            ))}
        </Grid>
    );
};

export default CustomerWishlistTab;
